// Library Methods
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useDevicesContext } from "src/contexts/devices/DevicesContext";

// Types
import { ExamType } from "src/services/exams/types";

// Hooks
import { useExamStatus } from "./useExamStatus";

// Utils
import {
  canEditExamSection,
  getCompletedExamSections,
  getCompletedExamsTooltip,
  getExamCardText,
  getTooltipText,
} from "src/utils/exams/examReportsHelper";

type ExamCardHelpersType = {
  exam: ExamType;
};

const useExamCardHelpers = ({ exam }: ExamCardHelpersType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const { devices } = useDevicesContext();

  const { getExamStatus } = useExamStatus();

  const deviceToUse = useMemo(
    () => devices?.find((item) => item?.associatedExam?.exam?.id === exam?.id),
    [devices, exam]
  );

  const examToUse = useMemo(
    () => deviceToUse?.associatedExam?.exam ?? exam,
    [deviceToUse, exam]
  );

  const examStatus = useMemo(
    () => getExamStatus({ device: deviceToUse, exam: examToUse }),
    [deviceToUse, examToUse, getExamStatus]
  );

  const examTypeText = useMemo(
    () => getExamCardText({ exam: examToUse, t }),
    [examToUse, t]
  );

  const examTooltipText = useMemo(
    () => getTooltipText(examToUse, t),
    [examToUse, t]
  );

  const completedExamsText = useMemo(
    () => getCompletedExamsTooltip(examToUse, t),
    [examToUse, t]
  );

  // return exam sections with a completion date
  const completedExamSections = useMemo(
    () => getCompletedExamSections(examToUse),
    [examToUse]
  );

  // check if live update button should be hidden
  const isLiveUpdateDisabled = useMemo(
    () =>
      !deviceToUse?.deviceStatus?.stage?.stageType ||
      !deviceToUse?.associatedExam?.exam,
    [deviceToUse?.deviceStatus, deviceToUse?.associatedExam]
  );

  // check if view report button should be hidden
  const isViewReportDisabled = useMemo(
    () => completedExamSections?.length === 0,
    [completedExamSections?.length]
  );

  // check if edit button should be hidden
  const isEditDisabled = useMemo(() => {
    const isInvalidSection = canEditExamSection(
      clinicInfo?.licensing,
      examToUse
    );
    const isPartiallyCompleteExam = completedExamSections?.length > 0;
    const isInvalidStatus = examStatus?.status !== t("Not Started");
    const hasDevice = Boolean(deviceToUse);

    return (
      isInvalidSection ||
      isPartiallyCompleteExam ||
      isInvalidStatus ||
      hasDevice
    );
  }, [
    examToUse,
    clinicInfo?.licensing,
    completedExamSections?.length,
    examStatus?.status,
    deviceToUse,
    t,
  ]);

  return {
    deviceToUse,
    examToUse,
    examStatus,
    examTooltipText,
    examTypeText,
    completedExamsText,
    isLiveUpdateDisabled,
    isViewReportDisabled,
    isEditDisabled,
  };
};

export default useExamCardHelpers;
